import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export interface PortfolioProject {
  id: string
  name: string
  status: string
  visitors: number
  pageviews: number
  uptimePercent: number | null
  isUp: boolean | null
  performanceScore: number | null
  openIssues: number
}

export interface PortfolioAlert {
  projectId: string
  projectName: string
  reason: string
  severity: 'critical' | 'warning'
}

export interface PortfolioTotals {
  visitors: number
  pageviews: number
  avgUptime: number | null
  avgPerformance: number | null
  openIssues: number
  projectCount: number
}

export interface PortfolioSummary {
  totals: PortfolioTotals
  projects: PortfolioProject[]
  needsAttention: PortfolioAlert[]
}

export function usePortfolioSummary(agencyId?: string) {
  const [data, setData] = useState<PortfolioSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        setLoading(true)
        setError(null)

        const params: Record<string, string> = {}
        if (agencyId) params.agencyId = agencyId

        const queryString = new URLSearchParams(params).toString()
        const url = queryString ? `agency-portfolio-summary?${queryString}` : 'agency-portfolio-summary'

        const { data: result, error: fnError } = await supabase.functions.invoke(url, {
          method: 'GET',
        })

        if (fnError) throw fnError
        if (result?.error) throw new Error(result.error)

        setData(result as PortfolioSummary)
      } catch (err) {
        console.error('Portfolio summary error:', err)
        setError(err instanceof Error ? err.message : 'Failed to load portfolio')
      } finally {
        setLoading(false)
      }
    }

    fetchPortfolio()
  }, [agencyId])

  return { data, loading, error }
}
